import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useFetch, apiPost, timeAgo } from "@/lib/shieldx";
import { PageHeader, Loader, Panel, Stat } from "@/components/shieldx/Primitives";
import { StatusBadge, RiskBadge, DemoTag } from "@/components/shieldx/Badges";
import { Table } from "@/components/shieldx/Table";
import { ShieldCheck, Globe, Bot, Plus, ArrowRight, Check } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const STEPS = [
  { key: "REGISTER", label: "Register", desc: "Add the website URL." },
  { key: "VERIFY", label: "Verify Ownership", desc: "Confirm you control the domain." },
  { key: "CONFIGURE", label: "Configure Protection", desc: "Connect the ShieldX protection boundary." },
  { key: "ACTIVATE", label: "Activate", desc: "Bring the protection boundary online." },
];

export default function Protection() {
  const assets = useFetch("/assets");
  const agents = useFetch("/ai-agents");
  const [tab, setTab] = useState("assets");
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  // onboarding state
  const [stepIdx, setStepIdx] = useState(0);
  const [url, setUrl] = useState("");
  const [asset, setAsset] = useState(null);
  const [busy, setBusy] = useState(false);

  if (assets.loading || agents.loading) return <Loader label="Loading protection boundary" />;

  const protectedCount = assets.data.filter((a) => a.state === "PROTECTED").length;
  const monitoring = assets.data.filter((a) => a.state === "MONITORING").length;
  const needsAttention = assets.data.filter((a) => ["VERIFICATION_REQUIRED", "CONFIGURATION_REQUIRED", "AT_RISK", "NOT_CONNECTED"].includes(a.state)).length;

  const resetFlow = () => { setStepIdx(0); setUrl(""); setAsset(null); };

  const doStep = async () => {
    setBusy(true);
    try {
      if (stepIdx === 0) {
        const a = await apiPost("/protection/register-website", { url });
        setAsset(a); setStepIdx(1); toast.success("Website registered — ownership verification required");
      } else {
        const stepKey = STEPS[stepIdx].key;
        const a = await apiPost(`/protection/${asset.id}/advance`, { step: stepKey });
        setAsset(a);
        if (stepIdx < STEPS.length - 1) setStepIdx(stepIdx + 1);
        else { toast.success("ShieldX protection boundary active"); setOpen(false); resetFlow(); assets.reload(); }
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Step failed"); } finally { setBusy(false); }
  };

  const assetCols = [
    { key: "name", label: "Asset", render: (r) => (
      <div><p className="font-medium text-slate-100">{r.name}</p><p className="font-mono text-[11px] text-slate-500">{r.type} · {r.environment}</p></div>
    ) },
    { key: "risk_level", label: "Risk", render: (r) => <RiskBadge value={r.risk_level} /> },
    { key: "state", label: "Protection", render: (r) => <StatusBadge value={r.protection_status || r.state} /> },
    { key: "last_check", label: "Last Check", render: (r) => <span className="text-xs text-slate-500">{timeAgo(r.last_check)}</span> },
  ];

  const agentCols = [
    { key: "name", label: "Identity", render: (r) => (
      <div><p className="font-medium text-slate-100">{r.name}</p><p className="font-mono text-[11px] text-slate-500">{r.model}</p></div>
    ) },
    { key: "data_access", label: "Data Access", render: (r) => <span className="text-xs text-slate-400">{r.data_access}</span> },
    { key: "risk_level", label: "Risk", render: (r) => <RiskBadge value={r.risk_level} /> },
    { key: "status", label: "Authorization", render: (r) => <StatusBadge value={r.status} /> },
  ];

  return (
    <div>
      <PageHeader icon={ShieldCheck} title="Protection"
        subtitle="Establish a controlled protection boundary around your registered digital assets. We never mark an asset PROTECTED until its boundary is actually configured."
        actions={
          <button onClick={() => { resetFlow(); setOpen(true); }} data-testid="protect-website"
            className="flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-500"><Plus className="h-4 w-4" /> Protect a Website</button>
        } />

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Stat label="Protected" value={protectedCount} tone="blue" />
        <Stat label="Monitoring" value={monitoring} tone="cyan" />
        <Stat label="Needs Attention" value={needsAttention} tone="amber" />
        <Stat label="AI Agent Identities" value={agents.data.length} tone="purple" />
      </div>

      <div className="mt-6 flex gap-2 border-b border-slate-800">
        <TabBtn active={tab === "assets"} onClick={() => setTab("assets")} testid="tab-assets" icon={Globe}>Protected Assets</TabBtn>
        <TabBtn active={tab === "agents"} onClick={() => setTab("agents")} testid="tab-agents" icon={Bot}>AI Agent Identities</TabBtn>
      </div>

      <div className="mt-4">
        {tab === "assets" ? (
          <Table columns={assetCols} rows={assets.data} testid="protection-assets-table" onRowClick={() => navigate("/app/assets")} />
        ) : (
          <>
            <p className="mb-3 text-sm text-slate-400">AI agents are controlled digital identities — capability never grants authority. Manage scope and revocation in the full registry.</p>
            <Table columns={agentCols} rows={agents.data} testid="protection-agents-table" onRowClick={() => navigate("/app/ai-agents")} />
          </>
        )}
      </div>

      <Dialog open={open} onOpenChange={(o) => { if (!o) resetFlow(); setOpen(o); }}>
        <DialogContent className="max-w-lg border-slate-800 bg-slate-900 text-slate-100">
          <DialogHeader>
            <DialogTitle className="font-display">Protect a Website</DialogTitle>
            <DialogDescription className="text-slate-400">A URL alone does not activate protection. Complete each step to bring the boundary online.</DialogDescription>
          </DialogHeader>

          <div className="flex items-center gap-1.5">
            {STEPS.map((s, i) => (
              <div key={s.key} className="flex flex-1 items-center gap-1.5">
                <div className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-bold ${i < stepIdx ? "bg-emerald-500/20 text-emerald-300" : i === stepIdx ? "bg-blue-600 text-white" : "bg-slate-800 text-slate-500"}`}>
                  {i < stepIdx ? <Check className="h-3.5 w-3.5" /> : i + 1}
                </div>
                {i < STEPS.length - 1 && <div className={`h-px flex-1 ${i < stepIdx ? "bg-emerald-500/40" : "bg-slate-800"}`} />}
              </div>
            ))}
          </div>

          <div className="rounded-lg border border-slate-800 bg-slate-900/60 p-4">
            <p className="font-display text-sm font-semibold text-slate-100">{STEPS[stepIdx].label}</p>
            <p className="mt-0.5 text-xs text-slate-500">{STEPS[stepIdx].desc}</p>

            {stepIdx === 0 && (
              <input value={url} onChange={(e) => setUrl(e.target.value)} data-testid="website-url" placeholder="https://yourwebsite.com"
                className="mt-3 h-10 w-full rounded-lg border border-slate-800 bg-slate-900/70 px-3 text-sm text-slate-100 outline-none focus:border-blue-500/50" />
            )}
            {stepIdx === 1 && (
              <div className="mt-3 space-y-2 text-xs text-slate-400">
                <p>Verify control of <span className="font-mono text-slate-200">{asset?.name}</span> using one of:</p>
                <ul className="ml-4 list-disc space-y-1"><li>DNS TXT record</li><li>HTML meta tag or verification file</li><li>Authorized integration</li></ul>
                <div className="mt-2 rounded-md border border-amber-500/20 bg-amber-500/5 px-3 py-2 text-amber-300">Current status: VERIFICATION REQUIRED</div>
              </div>
            )}
            {stepIdx === 2 && (
              <div className="mt-3 space-y-2 text-xs text-slate-400">
                <p>Connect the protection boundary via gateway / reverse proxy / API integration.</p>
                <div className="rounded-md border border-cyan-500/20 bg-cyan-500/5 px-3 py-2 text-cyan-300">Current status: CONFIGURATION REQUIRED → will move to MONITORING</div>
              </div>
            )}
            {stepIdx === 3 && (
              <div className="mt-3 space-y-2 text-xs text-slate-400">
                <p>Activate enforcement. ShieldX will evaluate authorization for protected actions.</p>
                <div className="rounded-md border border-emerald-500/20 bg-emerald-500/5 px-3 py-2 text-emerald-300">On activation: PROTECTED</div>
              </div>
            )}
          </div>

          <button onClick={doStep} disabled={busy || (stepIdx === 0 && !url.trim())} data-testid="protection-next"
            className="flex items-center justify-center gap-2 rounded-lg bg-blue-600 py-2.5 text-sm font-semibold text-white hover:bg-blue-500 disabled:opacity-50">
            {stepIdx === STEPS.length - 1 ? "Activate Protection" : "Continue"} <ArrowRight className="h-4 w-4" />
          </button>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function TabBtn({ active, onClick, children, testid, icon: Icon }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`flex items-center gap-2 border-b-2 px-4 py-2.5 text-sm font-medium transition-colors ${active ? "border-blue-500 text-blue-300" : "border-transparent text-slate-400 hover:text-slate-200"}`}>
      <Icon className="h-4 w-4" /> {children}
    </button>
  );
}
